// Polyfills for browsers that don't support ES2022/2023 APIs (e.g. Chrome <97, Safari <15.4)
// core-js checks internally whether each API exists before patching, so no double-polyfill risk.
import 'core-js/stable/array/at';
import 'core-js/stable/array/find-last';
import 'core-js/stable/array/find-last-index';
import 'core-js/stable/string/at';
import 'core-js/stable/object/has-own';
import 'core-js/stable/structured-clone';

import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { LinearGradient } from 'expo-linear-gradient';
import Head from 'expo-router/head';
import { AuthProvider, useAuth } from '@/src/context/AuthContext';
import { ThemeProvider, useTheme } from '@/src/context/ThemeContext';
import { NotificationProvider, useNotifications } from '@/src/context/NotificationContext';
import { SubscriptionProvider, useSubscription } from '@/src/context/SubscriptionContext';
import { LanguageProvider, useTranslation } from '@/src/i18n';
import { getPendingFriendRequestsUseCase, getChallengesReceivedUseCase, getInvitationsReceivedUseCase, getTeamInvitationsUseCase, getUserTrainingStatsUseCase } from '@/src/core/dependencies';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { useRouter, useSegments, usePathname } from 'expo-router';
import { Drawer } from 'expo-router/drawer';
import { StatusBar } from 'expo-status-bar';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDrawerStatus } from '@react-navigation/drawer';
import { useNavigation } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { ActivityIndicator, Animated, Image, Platform, ScrollView, StyleSheet, Text, TouchableOpacity, useWindowDimensions, View } from 'react-native';
import { platformAlert } from '@/src/core/utils/platformAlert';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import * as Sentry from '@sentry/react-native';
import env from '@/src/core/config/env';
import { APP_VERSION } from '@/src/core/config/appVersion';
import { ErrorBoundary } from '@/src/presentation/components/ErrorBoundary';
import { logger } from '@/src/core/utils/logger';
import { navigationHistory } from '@/src/core/utils/navigationHistory';

Sentry.init({
  dsn: env.sentryDsn,
  enabled: !__DEV__,
  environment: env.appEnv,
  tracesSampleRate: env.isProduction ? 0.2 : 1.0,
  ignoreErrors: [
    // Expected auth errors — user-facing, not actionable by developers
    'Invalid login credentials',
    'Email not confirmed',
    'User not authenticated',
    'Unable to validate email address: invalid format',
    // Network errors from regions with poor Supabase connectivity (e.g. China firewall)
    '6000ms timeout exceeded',
    'signal is aborted without reason',
  ],
});

function CustomDrawer(props: any) {
  const { user, signOut } = useAuth();
  const { colors, isDark } = useTheme();
  const { t } = useTranslation();
  const { isPro } = useSubscription();
  const insets = useSafeAreaInsets();
  const router = useRouter();
  const [friendRequestsCount, setFriendRequestsCount] = useState(0);
  const [challengesCount, setChallengesCount] = useState(0);
  const [invitationsCount, setInvitationsCount] = useState(0);
  const [hasTrainingData, setHasTrainingData] = useState(false);
  const { unreadCount: notificationCount, refreshUnreadCount } = useNotifications();
  const drawerStatus = useDrawerStatus();

  useEffect(() => {
    if (user) {
      loadBadgeCounts();
    } else {
      // Clear badge counts when user logs out
      setFriendRequestsCount(0);
      setChallengesCount(0);
      setInvitationsCount(0);
      setHasTrainingData(false);
    }
  }, [user]);

  // Reload badge counts when drawer opens
  useEffect(() => {
    if (drawerStatus === 'open') {
      loadBadgeCounts();
      refreshUnreadCount();
    }
  }, [drawerStatus]);

  const loadBadgeCounts = async () => {
    if (!user) return;

    try {
      const [friendRequests, challenges, invitations, teamInvitations, trainingStats] = await Promise.all([
        getPendingFriendRequestsUseCase.execute(user.id, 50, 0).catch(() => []),
        getChallengesReceivedUseCase.execute(user.id, 50, 0).catch(() => []),
        getInvitationsReceivedUseCase.execute(user.id, 50, 0).catch(() => []),
        getTeamInvitationsUseCase.execute(user.id).catch(() => ({ received: [], sent: [] })),
        getUserTrainingStatsUseCase.execute(user.id).catch(() => null),
      ]);

      setFriendRequestsCount(friendRequests.length);
      setChallengesCount(challenges.filter(c => c.status === 'pending').length);
      const pendingTournamentInvitations = invitations.filter(i => i.status === 'pending').length;
      const pendingTeamInvitations = teamInvitations.received.filter(i => i.status === 'pending').length;
      setInvitationsCount(pendingTournamentInvitations + pendingTeamInvitations);
      setHasTrainingData((trainingStats?.totalLaunches ?? 0) > 0);
    } catch (error) {
      logger.error('Drawer', 'Failed to load badge counts', { error });
      // Set to 0 on error to avoid crashing the app
      setFriendRequestsCount(0);
      setChallengesCount(0);
      setInvitationsCount(0);
    }
  };

  const getInitials = () => {
    if (!user?.email) return '?';
    return user.email.substring(0, 2).toUpperCase();
  };

  const handleSignOut = () => {
    platformAlert(t('auth.signOut'), t('common.confirm') + '?', [
      { text: t('common.cancel'), style: 'cancel' },
      {
        text: t('auth.signOut'),
        style: 'destructive',
        onPress: async () => {
          await signOut();
          props.navigation.closeDrawer();
        },
      },
    ]);
  };

  const navigate = (route: string) => {
    props.navigation.closeDrawer();
    navigationHistory._reset();
    router.push(route as any);
  };

  return (
    <ScrollView style={[styles.drawer, { backgroundColor: colors.background }]}>
      {/* Profile Section */}
      <TouchableOpacity onPress={() => navigate(`/profile/${user?.id}`)} activeOpacity={0.85}>
        <LinearGradient
          colors={isDark ? ['#0D2540', '#2A5F9E'] : ['#7A4A22', '#C4873B']}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={[styles.profileGradient, { paddingTop: insets.top, borderBottomColor: 'rgba(255,255,255,0.15)' }]}
        >
          <View style={styles.profileContent}>
            <View style={styles.avatarRing}>
              {user?.user_metadata?.avatar_url ? (
                <Image source={{ uri: user.user_metadata.avatar_url }} style={styles.avatarImage} />
              ) : (
                <View style={styles.avatar}>
                  <Text style={styles.avatarText}>{getInitials()}</Text>
                </View>
              )}
            </View>
            <View style={styles.profileInfo}>
              <Text style={styles.profileName} numberOfLines={1}>
                {user?.user_metadata?.full_name || t('navigation.profile')}
              </Text>
              <Text style={styles.profileEmail} numberOfLines={1}>
                {user?.email || ''}
              </Text>
            </View>
            <Ionicons name="chevron-forward" size={20} color="rgba(255,255,255,0.6)" />
          </View>
        </LinearGradient>
      </TouchableOpacity>

      {/* Menu Items */}
      <View style={styles.menu}>

        {/* — General — */}
        <TouchableOpacity
          style={[styles.menuItem, { borderBottomColor: colors.divider }]}
          onPress={() => navigate('/')}
        >
          <Ionicons name="home-outline" size={24} color={colors.text} style={styles.menuIcon} />
          <Text style={[styles.menuText, { color: colors.text }]}>{t('navigation.home')}</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.menuItem, { borderBottomColor: colors.divider }]}
          onPress={() => navigate('/notifications')}
        >
          <Ionicons name="notifications-outline" size={24} color={colors.text} style={styles.menuIcon} />
          <Text style={[styles.menuText, { color: colors.text }]}>{t('notifications.title')}</Text>
          {notificationCount > 0 && (
            <View style={[styles.badge, { backgroundColor: colors.error }]}>
              <Text style={styles.badgeText}>{notificationCount > 99 ? '99+' : notificationCount}</Text>
            </View>
          )}
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.menuItem, { borderBottomColor: colors.divider }]}
          onPress={() => navigate('/invitations')}
        >
          <Ionicons name="mail-outline" size={24} color={colors.text} style={styles.menuIcon} />
          <Text style={[styles.menuText, { color: colors.text }]}>{t('navigation.invitations')}</Text>
          {invitationsCount > 0 && (
            <View style={[styles.badge, { backgroundColor: colors.primary }]}>
              <Text style={styles.badgeText}>{invitationsCount}</Text>
            </View>
          )}
        </TouchableOpacity>

        {/* — Tournaments — */}
        <View style={[styles.sectionHeader, { marginTop: 10 }]}>
          <Text style={[styles.sectionTitle, { color: colors.textSecondary }]}>{t('navigation.tournamentsSection').toUpperCase()}</Text>
        </View>

        <TouchableOpacity
          style={[styles.menuItem, { borderBottomColor: colors.divider }]}
          onPress={() => navigate('/my-tournaments')}
        >
          <MaterialCommunityIcons name="tournament" size={24} color={colors.text} style={styles.menuIcon} />
          <Text style={[styles.menuText, { color: colors.text }]}>{t('navigation.myTournaments')}</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.menuItem, { borderBottomColor: colors.divider }]}
          onPress={() => navigate('/explore')}
        >
          <Ionicons name="compass-outline" size={24} color={colors.text} style={styles.menuIcon} />
          <Text style={[styles.menuText, { color: colors.text }]}>{t('navigation.explore')}</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.menuItem, { borderBottomColor: colors.divider }]}
          onPress={() => navigate('/join-tournament')}
        >
          <Ionicons name="key-outline" size={24} color={colors.text} style={styles.menuIcon} />
          <Text style={[styles.menuText, { color: colors.text }]}>{t('navigation.joinByCode')}</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.menuItem, { borderBottomColor: colors.divider }]}
          onPress={() => navigate('/create-tournament')}
        >
          <Ionicons name="add-circle-outline" size={24} color={colors.text} style={styles.menuIcon} />
          <Text style={[styles.menuText, { color: colors.text }]}>{t('tournaments.createTournament')}</Text>
        </TouchableOpacity>

        {/* — Leagues — */}
        <View style={[styles.sectionHeader, { marginTop: 10 }]}>
          <Text style={[styles.sectionTitle, { color: colors.textSecondary }]}>{t('navigation.leaguesSection').toUpperCase()}</Text>
        </View>

        <TouchableOpacity
          style={[styles.menuItem, { borderBottomColor: colors.divider }]}
          onPress={() => navigate('/leagues')}
        >
          <MaterialCommunityIcons name="trophy-outline" size={24} color={colors.text} style={styles.menuIcon} />
          <Text style={[styles.menuText, { color: colors.text }]}>{t('navigation.leagues')}</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.menuItem, { borderBottomColor: colors.divider }]}
          onPress={() => navigate('/explore-leagues')}
        >
          <Ionicons name="compass-outline" size={24} color={colors.text} style={styles.menuIcon} />
          <Text style={[styles.menuText, { color: colors.text }]}>{t('navigation.exploreLeagues')}</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.menuItem, { borderBottomColor: colors.divider }]}
          onPress={() => navigate('/create-league')}
        >
          <Ionicons name="add-circle-outline" size={24} color={colors.text} style={styles.menuIcon} />
          <Text style={[styles.menuText, { color: colors.text }]}>{t('leagues.createLeague')}</Text>
        </TouchableOpacity>

        {/* — Teams — */}
        <View style={[styles.sectionHeader, { marginTop: 10 }]}>
          <Text style={[styles.sectionTitle, { color: colors.textSecondary }]}>{t('navigation.teamsSection').toUpperCase()}</Text>
        </View>

        <TouchableOpacity
          style={[styles.menuItem, { borderBottomColor: colors.divider }]}
          onPress={() => navigate('/teams')}
        >
          <Ionicons name="people-outline" size={24} color={colors.text} style={styles.menuIcon} />
          <Text style={[styles.menuText, { color: colors.text }]}>{t('navigation.teams')}</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.menuItem, { borderBottomColor: colors.divider }]}
          onPress={() => navigate('/explore-teams')}
        >
          <Ionicons name="compass-outline" size={24} color={colors.text} style={styles.menuIcon} />
          <Text style={[styles.menuText, { color: colors.text }]}>{t('navigation.exploreTeams')}</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.menuItem, { borderBottomColor: colors.divider }]}
          onPress={() => navigate('/create-team')}
        >
          <Ionicons name="add-circle-outline" size={24} color={colors.text} style={styles.menuIcon} />
          <Text style={[styles.menuText, { color: colors.text }]}>{t('teams.createTeam')}</Text>
        </TouchableOpacity>

        {/* — TrainerPass — */}
        {hasTrainingData && (
          <>
            <View style={[styles.sectionHeader, { marginTop: 10 }]}>
              <Text style={[styles.sectionTitle, { color: colors.textSecondary }]}>{t('navigation.trainerPassSection').toUpperCase()}</Text>
            </View>

            <TouchableOpacity
              style={[styles.menuItem, { borderBottomColor: colors.divider }]}
              onPress={() => navigate('/my-launches')}
            >
              <MaterialCommunityIcons name="rocket-launch-outline" size={24} color={colors.text} style={styles.menuIcon} />
              <Text style={[styles.menuText, { color: colors.text }]}>{t('navigation.myLaunches')}</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.menuItem, { borderBottomColor: colors.divider }]}
              onPress={() => navigate('/leaderboard')}
            >
              <MaterialCommunityIcons name="podium" size={24} color={colors.text} style={styles.menuIcon} />
              <Text style={[styles.menuText, { color: colors.text }]}>{t('navigation.trainerPassLeaderboard')}</Text>
            </TouchableOpacity>
          </>
        )}

        {/* — Social — */}
        <View style={[styles.sectionHeader, { marginTop: 10 }]}>
          <Text style={[styles.sectionTitle, { color: colors.textSecondary }]}>{t('settings.social').toUpperCase()}</Text>
        </View>

        <TouchableOpacity
          style={[styles.menuItem, { borderBottomColor: colors.divider }]}
          onPress={() => navigate('/feed')}
        >
          <Ionicons name="newspaper-outline" size={24} color={colors.text} style={styles.menuIcon} />
          <Text style={[styles.menuText, { color: colors.text }]}>{t('navigation.feed')}</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.menuItem, { borderBottomColor: colors.divider }]}
          onPress={() => navigate('/friends')}
        >
          <Ionicons name="people-outline" size={24} color={colors.text} style={styles.menuIcon} />
          <Text style={[styles.menuText, { color: colors.text }]}>{t('navigation.friends')}</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.menuItem, { borderBottomColor: colors.divider }]}
          onPress={() => navigate('/friend-requests')}
        >
          <Ionicons name="person-add-outline" size={24} color={colors.text} style={styles.menuIcon} />
          <Text style={[styles.menuText, { color: colors.text }]}>{t('navigation.friendRequests')}</Text>
          {friendRequestsCount > 0 && (
            <View style={[styles.badge, { backgroundColor: colors.error }]}>
              <Text style={styles.badgeText}>{friendRequestsCount}</Text>
            </View>
          )}
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.menuItem, { borderBottomColor: colors.divider }]}
          onPress={() => navigate('/challenges')}
        >
          <MaterialCommunityIcons name="sword-cross" size={24} color={colors.text} style={styles.menuIcon} />
          <Text style={[styles.menuText, { color: colors.text }]}>{t('navigation.challenges')}</Text>
          {challengesCount > 0 && (
            <View style={[styles.badge, { backgroundColor: '#FFD700' }]}>
              <Text style={[styles.badgeText, { color: '#8B4513' }]}>{challengesCount}</Text>
            </View>
          )}
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.menuItem, { borderBottomColor: colors.divider }]}
          onPress={() => navigate('/search-users')}
        >
          <Ionicons name="search-outline" size={24} color={colors.text} style={styles.menuIcon} />
          <Text style={[styles.menuText, { color: colors.text }]}>{t('navigation.searchUsers')}</Text>
        </TouchableOpacity>

        {/* — Other — */}
        <View style={[styles.sectionHeader, { marginTop: 10 }]}>
          <Text style={[styles.sectionTitle, { color: colors.textSecondary }]}>{t('settings.other').toUpperCase()}</Text>
        </View>

        <TouchableOpacity
          style={[styles.menuItem, { borderBottomColor: colors.divider }]}
          onPress={() => navigate('/paywall')}
        >
          <Ionicons name={isPro ? 'star' : 'star-outline'} size={24} color={isPro ? '#FFD700' : colors.text} style={styles.menuIcon} />
          <Text style={[styles.menuText, { color: colors.text }]}>
            {isPro ? t('subscription.manageSubscription') : t('subscription.upgradeToPro')}
          </Text>
          {isPro && (
            <View style={[styles.badge, { backgroundColor: '#FFD700' }]}>
              <Text style={[styles.badgeText, { color: '#8B4513' }]}>PRO</Text>
            </View>
          )}
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.menuItem, { borderBottomColor: colors.divider }]}
          onPress={() => navigate('/settings')}
        >
          <Ionicons name="settings-outline" size={24} color={colors.text} style={styles.menuIcon} />
          <Text style={[styles.menuText, { color: colors.text }]}>{t('navigation.settings')}</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.menuItem, { borderBottomColor: colors.divider }]}
          onPress={() => navigate('/whats-new')}
        >
          <Ionicons name="sparkles-outline" size={24} color={colors.text} style={styles.menuIcon} />
          <Text style={[styles.menuText, { color: colors.text }]}>{t('navigation.whatsNew')}</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.menuItem, { borderBottomColor: colors.divider }]}
          onPress={() => navigate('/feedback')}
        >
          <Ionicons name="chatbox-ellipses-outline" size={24} color={colors.text} style={styles.menuIcon} />
          <Text style={[styles.menuText, { color: colors.text }]}>{t('navigation.feedback')}</Text>
        </TouchableOpacity>

      </View>

      {/* Bottom Section */}
      <View style={[styles.bottom, { borderTopColor: colors.divider }]}>
        <TouchableOpacity style={[styles.menuItem, { borderBottomWidth: 0 }]} onPress={handleSignOut}>
          <Ionicons name="log-out" size={24} color={colors.error} style={styles.menuIcon} />
          <Text style={[styles.menuText, { color: colors.error }]}>{t('navigation.logout')}</Text>
        </TouchableOpacity>
      </View>

      <View style={styles.version}>
        <Text style={[styles.versionText, { color: colors.textTertiary }]}>Tournaly v{APP_VERSION}</Text>
      </View>
    </ScrollView>
  );
}

function BackButton({ fallback = '/' }: { fallback?: string }) {
  const router = useRouter();
  const { colors } = useTheme();
  const navigation = useNavigation();

  // Show back if there is a real previous screen to return to.
  // Drawer navigation resets the history, so length === 1 means we arrived from the drawer.
  const showBack = navigationHistory.length > 1;

  if (!showBack) {
    return (
      <TouchableOpacity
        onPress={() => (navigation as any).openDrawer()}
        style={{ marginLeft: 8, padding: 4 }}
      >
        <Ionicons name="menu" size={24} color={colors.text} />
      </TouchableOpacity>
    );
  }

  return (
    <TouchableOpacity
      onPress={() => {
        navigationHistory.pop();
        const previous = navigationHistory.current();
        router.replace((previous ?? fallback) as any);
      }}
      style={{ marginLeft: 8, padding: 4 }}
    >
      <Ionicons name="arrow-back" size={24} color={colors.text} />
    </TouchableOpacity>
  );
}

function DrawerNavigator() {
  const { colors, isDark } = useTheme();
  const { t } = useTranslation();
  const { user, loading } = useAuth();
  const segments = useSegments();
  const router = useRouter();
  const { width } = useWindowDimensions();
  const pathname = usePathname();
  const screenOpacity = useRef(new Animated.Value(1)).current;

  // Track every route change so BackButton can navigate to the real previous screen.
  // usePathname() returns the resolved path (e.g. /tournament/123, not /tournament/[id]).
  useEffect(() => {
    navigationHistory.push(pathname);
  }, [pathname]);

  // Subtle fade transition on every screen navigation
  useEffect(() => {
    screenOpacity.setValue(0.93);
    Animated.timing(screenOpacity, {
      toValue: 1,
      duration: 200,
      useNativeDriver: true,
    }).start();
  }, [pathname]);

  // On web with large screens (>= 900px), keep drawer permanently visible — but NOT on auth screens
  const inAuthGroup = segments[0] === 'auth';
  const isLargeScreen = Platform.OS === 'web' && width >= 900;
  const drawerType = isLargeScreen && !inAuthGroup && !!user ? 'permanent' : 'front';

  useEffect(() => {
    if (loading) return;
    if (!user && !inAuthGroup) {
      if (Platform.OS === 'web') {
        // On web, redirect to root — index.tsx shows the landing for unauthenticated users
        // Exceptions: delete-account (required by stores), post/[id] (public sharing page), blog, use-case landing pages
        const isPublicRoute =
          pathname === '/delete-account' ||
          pathname.startsWith('/post/') ||
          pathname === '/blog' ||
          pathname.startsWith('/blog/') ||
          pathname === '/tournament-bracket-maker' ||
          pathname === '/single-elimination-bracket' ||
          pathname === '/double-elimination-bracket' ||
          pathname === '/round-robin-tournament' ||
          pathname === '/swiss-system-tournament' ||
          pathname === '/king-of-the-hill-tournament';
        if (pathname !== '/' && !isPublicRoute) router.replace('/');
      } else {
        // On mobile, redirect to welcome screen — except delete-account (required by stores),
        // welcome itself (to avoid redirect loop), and post/[id] (public sharing page)
        const isPublicRoute = pathname === '/delete-account' || pathname.startsWith('/post/');
        if (!isPublicRoute && pathname !== '/welcome') router.replace('/welcome');
      }
    } else if (user && inAuthGroup && segments[1] !== 'reset-password') {
      router.replace('/');
    }
  }, [user, loading, segments]);

  // Skip auth loading spinner for public routes — they render immediately without auth
  const isPublicRoute =
    pathname === '/delete-account' ||
    pathname.startsWith('/post/') ||
    pathname === '/blog' ||
    pathname.startsWith('/blog/') ||
    pathname === '/tournament-bracket-maker' ||
    pathname === '/single-elimination-bracket' ||
    pathname === '/double-elimination-bracket' ||
    pathname === '/round-robin-tournament' ||
    pathname === '/swiss-system-tournament' ||
    pathname === '/king-of-the-hill-tournament';
  if (loading && !isPublicRoute) {
    return (
      <View style={[styles.loading, { backgroundColor: colors.background }]}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  // Using user?.id as key forces all components to remount when user changes
  // This clears all cached state (tournaments, friends, etc.) on logout/login
  return (
    <GestureHandlerRootView key={user?.id || 'logged-out'} style={{ flex: 1 }}>
      <StatusBar style={isDark ? 'light' : 'dark'} />
      <Animated.View style={{ flex: 1, opacity: screenOpacity }}>
      <Drawer
        drawerContent={(props) => <CustomDrawer {...props} />}
        screenOptions={{
          headerStyle: {
            backgroundColor: colors.surface,
            borderBottomColor: colors.border,
            borderBottomWidth: 1,
            // @ts-ignore - web-specific shadow removal
            boxShadow: 'none',
          },
          headerShadowVisible: false,
          headerTintColor: colors.text,
          headerTitleStyle: { fontWeight: 'bold' },
          drawerStyle: {
            backgroundColor: colors.background,
            width: 280,
            borderRightColor: colors.border,
            borderRightWidth: 1,
          },
          drawerType,
          overlayColor: drawerType === 'permanent' ? 'transparent' : undefined,
          swipeEnabled: !inAuthGroup && !!user,
        }}
      >
        <Drawer.Screen name="welcome" options={{ headerShown: false, drawerItemStyle: { display: 'none' } }} />
        <Drawer.Screen name="auth/login" options={{ headerShown: false, drawerItemStyle: { display: 'none' }, drawerLockMode: 'locked-closed' }} />
        <Drawer.Screen name="auth/register" options={{ headerShown: false, drawerItemStyle: { display: 'none' }, drawerLockMode: 'locked-closed' }} />
        <Drawer.Screen name="auth/forgot-password" options={{ headerShown: false, drawerItemStyle: { display: 'none' }, drawerLockMode: 'locked-closed' }} />
        <Drawer.Screen name="auth/reset-password" options={{ headerShown: false, drawerItemStyle: { display: 'none' }, drawerLockMode: 'locked-closed' }} />
        <Drawer.Screen name="index" options={{ title: 'Tournaly', drawerItemStyle: { display: 'none' }, headerShown: !!user }} />
        <Drawer.Screen name="my-tournaments" options={{ title: t('navigation.myTournaments'), drawerItemStyle: { display: 'none' } }} />
        <Drawer.Screen name="explore" options={{ title: t('navigation.explore'), drawerItemStyle: { display: 'none' } }} />
        <Drawer.Screen name="settings" options={{ title: t('navigation.settings'), drawerItemStyle: { display: 'none' } }} />
        <Drawer.Screen name="create-tournament" options={{ title: t('tournaments.createTournament'), drawerItemStyle: { display: 'none' }, headerLeft: () => <BackButton /> }} />
        <Drawer.Screen name="edit-profile" options={{ title: t('profile.editProfile'), drawerItemStyle: { display: 'none' }, headerLeft: () => <BackButton /> }} />
        <Drawer.Screen name="edit-tournament/[id]" options={{ title: t('tournaments.editTournament'), drawerItemStyle: { display: 'none' }, headerLeft: () => <BackButton /> }} />
        <Drawer.Screen name="tournament/[id]" options={{ title: t('tournaments.tournament'), drawerItemStyle: { display: 'none' }, headerLeft: () => <BackButton /> }} />
        <Drawer.Screen name="bracket/[id]" options={{ title: t('tournaments.bracket'), drawerItemStyle: { display: 'none' }, headerLeft: () => <BackButton /> }} />
        <Drawer.Screen name="profile/[userId]" options={{ title: t('navigation.profile'), drawerItemStyle: { display: 'none' }, headerLeft: () => <BackButton /> }} />
        <Drawer.Screen name="public/[id]" options={{ title: t('tournaments.tournament'), drawerItemStyle: { display: 'none' }, headerLeft: () => <BackButton fallback="/explore" /> }} />
        <Drawer.Screen name="post/[id]" options={{ headerShown: false, drawerItemStyle: { display: 'none' } }} />
        <Drawer.Screen name="friends" options={{ title: t('navigation.friends'), drawerItemStyle: { display: 'none' } }} />
        <Drawer.Screen name="friend-requests" options={{ title: t('navigation.friendRequests'), drawerItemStyle: { display: 'none' } }} />
        <Drawer.Screen name="challenges" options={{ title: t('navigation.challenges'), drawerItemStyle: { display: 'none' } }} />
        <Drawer.Screen name="followers/[userId]" options={{ title: t('navigation.followers'), drawerItemStyle: { display: 'none' }, headerLeft: () => <BackButton /> }} />
        <Drawer.Screen name="following/[userId]" options={{ title: t('navigation.following'), drawerItemStyle: { display: 'none' }, headerLeft: () => <BackButton /> }} />
        <Drawer.Screen
          name="feed"
          options={{
            title: t('navigation.feed'),
            drawerItemStyle: { display: 'none' },
            headerRight: () => (
              <TouchableOpacity
                onPress={() => router.push('/create-post')}
                style={{ marginRight: 16 }}
              >
                <Ionicons name="add-circle-outline" size={26} color={colors.primary} />
              </TouchableOpacity>
            ),
          }}
        />
        <Drawer.Screen name="search-users" options={{ title: t('navigation.searchUsers'), drawerItemStyle: { display: 'none' } }} />
        <Drawer.Screen name="create-post" options={{ title: t('feed.createPost'), drawerItemStyle: { display: 'none' }, headerLeft: () => <BackButton fallback="/feed" /> }} />
        <Drawer.Screen name="leagues" options={{ title: t('navigation.leagues'), drawerItemStyle: { display: 'none' } }} />
        <Drawer.Screen name="explore-leagues" options={{ title: t('leagues.exploreLeagues'), drawerItemStyle: { display: 'none' } }} />
        <Drawer.Screen name="create-league" options={{ title: t('leagues.createLeague'), drawerItemStyle: { display: 'none' }, headerLeft: () => <BackButton fallback="/leagues" /> }} />
        <Drawer.Screen name="league/[id]" options={{ title: t('leagues.league'), drawerItemStyle: { display: 'none' }, headerLeft: () => <BackButton fallback="/leagues" /> }} />
        <Drawer.Screen name="teams" options={{ title: t('navigation.teams'), drawerItemStyle: { display: 'none' } }} />
        <Drawer.Screen name="explore-teams" options={{ title: t('navigation.exploreTeams'), drawerItemStyle: { display: 'none' } }} />
        <Drawer.Screen name="create-team" options={{ title: t('teams.createTeam'), drawerItemStyle: { display: 'none' }, headerLeft: () => <BackButton fallback="/teams" /> }} />
        <Drawer.Screen name="team/[id]" options={{ title: t('teams.team'), drawerItemStyle: { display: 'none' }, headerLeft: () => <BackButton fallback="/teams" /> }} />
        <Drawer.Screen name="leaderboard" options={{ title: t('navigation.trainerPassLeaderboard'), drawerItemStyle: { display: 'none' } }} />
        <Drawer.Screen name="my-launches" options={{ title: t('navigation.myLaunches'), drawerItemStyle: { display: 'none' } }} />
        <Drawer.Screen name="notifications" options={{ title: t('notifications.title'), drawerItemStyle: { display: 'none' } }} />
        <Drawer.Screen name="invitations" options={{ title: t('navigation.invitations'), drawerItemStyle: { display: 'none' } }} />
        <Drawer.Screen name="join-tournament" options={{ title: t('navigation.joinByCode'), drawerItemStyle: { display: 'none' } }} />
        <Drawer.Screen name="paywall" options={{ title: t('subscription.upgradeToPro'), drawerItemStyle: { display: 'none' }, headerLeft: () => <BackButton /> }} />
        <Drawer.Screen name="whats-new" options={{ title: t('navigation.whatsNew'), drawerItemStyle: { display: 'none' } }} />
        <Drawer.Screen name="feedback" options={{ title: t('navigation.feedback'), drawerItemStyle: { display: 'none' } }} />
        <Drawer.Screen name="onboarding" options={{ headerShown: false, drawerItemStyle: { display: 'none' } }} />
        <Drawer.Screen name="delete-account" options={{ headerShown: false, drawerItemStyle: { display: 'none' } }} />
        <Drawer.Screen name="blog" options={{ headerShown: false, drawerItemStyle: { display: 'none' } }} />
        <Drawer.Screen name="blog/[slug]" options={{ headerShown: false, drawerItemStyle: { display: 'none' } }} />
        <Drawer.Screen name="tournament-bracket-maker" options={{ headerShown: false, drawerItemStyle: { display: 'none' } }} />
        <Drawer.Screen name="single-elimination-bracket" options={{ headerShown: false, drawerItemStyle: { display: 'none' } }} />
        <Drawer.Screen name="double-elimination-bracket" options={{ headerShown: false, drawerItemStyle: { display: 'none' } }} />
        <Drawer.Screen name="round-robin-tournament" options={{ headerShown: false, drawerItemStyle: { display: 'none' } }} />
        <Drawer.Screen name="swiss-system-tournament" options={{ headerShown: false, drawerItemStyle: { display: 'none' } }} />
        <Drawer.Screen name="king-of-the-hill-tournament" options={{ headerShown: false, drawerItemStyle: { display: 'none' } }} />
      </Drawer>
      </Animated.View>
    </GestureHandlerRootView>
  );
}

const GA4_MEASUREMENT_ID = process.env.EXPO_PUBLIC_GA4_MEASUREMENT_ID;

function GoogleAnalytics() {
  useEffect(() => {
    if (!GA4_MEASUREMENT_ID) return;
    const s1 = document.createElement('script');
    s1.async = true;
    s1.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
    document.head.appendChild(s1);
    const s2 = document.createElement('script');
    s2.innerHTML = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${GA4_MEASUREMENT_ID}');`;
    document.head.appendChild(s2);
  }, []);
  return null;
}

function RootLayout() {
  return (
    <Head.Provider>
      <ErrorBoundary>
        <ThemeProvider>
          <LanguageProvider>
            <AuthProvider>
              <SubscriptionProvider>
                <NotificationProvider>
                  <DrawerNavigator />
                  {Platform.OS === 'web' && <Analytics />}
                  {Platform.OS === 'web' && <SpeedInsights />}
                  {Platform.OS === 'web' && <GoogleAnalytics />}
                </NotificationProvider>
              </SubscriptionProvider>
            </AuthProvider>
          </LanguageProvider>
        </ThemeProvider>
      </ErrorBoundary>
    </Head.Provider>
  );
}

export default Sentry.wrap(RootLayout);

const styles = StyleSheet.create({
  loading: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  drawer: { flex: 1 },
  profileGradient: { borderBottomWidth: 1 },
  profileContent: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 20, paddingVertical: 20 },
  avatarRing: { width: 68, height: 68, borderRadius: 34, borderWidth: 2, borderColor: 'rgba(255,255,255,0.35)', overflow: 'hidden' },
  avatar: { width: 64, height: 64, borderRadius: 32, justifyContent: 'center', alignItems: 'center', backgroundColor: 'rgba(0,0,0,0.25)' },
  avatarImage: { width: 64, height: 64, borderRadius: 32 },
  avatarText: { fontSize: 24, fontWeight: 'bold', color: '#fff' },
  profileInfo: { flex: 1, marginLeft: 14 },
  profileName: { fontSize: 17, fontWeight: '700', color: '#fff', marginBottom: 3 },
  profileEmail: { fontSize: 13, color: 'rgba(255,255,255,0.72)' },
  menu: { paddingTop: 10 },
  sectionHeader: { paddingHorizontal: 20, paddingVertical: 8, paddingTop: 16 },
  sectionTitle: { fontSize: 12, fontWeight: '600', letterSpacing: 1 },
  menuItem: { flexDirection: 'row', alignItems: 'center', padding: 20, paddingVertical: 16, borderBottomWidth: 1 },
  menuIcon: { width: 40 },
  menuText: { fontSize: 16, flex: 1 },
  badge: {
    backgroundColor: '#FF3B30',
    borderRadius: 10,
    minWidth: 20,
    height: 20,
    paddingHorizontal: 6,
    justifyContent: 'center',
    alignItems: 'center',
  },
  badgeText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: 'bold',
  },
  bottom: { marginTop: 10, paddingTop: 10, borderTopWidth: 1 },
  version: { padding: 20, alignItems: 'center' },
  versionText: { fontSize: 12 },
});